import { Check } from 'lucide-react'

import { MultipleDropzone } from '@/components/ui/multiple-dropzone'
import type { ClaimPhotoAttachment } from '../../../../models/claim'

/** Photos straight off a phone, plus PDFs (police report, repair estimate). */
export const CLAIM_UPLOAD_ACCEPT = '.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.pdf'

interface ClaimPhotoUploaderProps {
  files: File[]
  onChange: (files: File[]) => void
  /** Called with only the newly-added files (for upload + EXIF side effects). */
  onNewFiles?: (files: File[]) => void
  error?: string
  /** Whether the files are already persisted on the backend (wording only). */
  uploaded?: boolean
  /** Files already on the claim (edit mode) — listed by name. */
  existingPhotos?: ClaimPhotoAttachment[]
}

/** Presentational file picker: dropzone + status. No form/API. */
export function ClaimPhotoUploader({
  files,
  onChange,
  onNewFiles,
  error,
  uploaded,
  existingPhotos = [],
}: ClaimPhotoUploaderProps) {
  const total = files.length

  function handleFilesChange(next: File[]) {
    const known = new Set(files.map((f) => `${f.name}:${f.size}`))
    onChange(next)
    const added = next.filter((f) => !known.has(`${f.name}:${f.size}`))
    if (added.length > 0) onNewFiles?.(added)
  }

  return (
    <div className="flex flex-col gap-6">
      {total > 0 && (
        <div className="flex items-center gap-2 text-sm text-green-700">
          <Check className="h-4 w-4" />
          <span>
            {total} file{total !== 1 ? 's' : ''} {uploaded ? 'uploaded' : 'added'}
          </span>
        </div>
      )}

      <MultipleDropzone
        onChange={handleFilesChange}
        accept={CLAIM_UPLOAD_ACCEPT}
        maxSizeMB={20}
        maxWidth={700}
        uploadedFiles={existingPhotos.map((p) => ({ name: p.fileName, url: p.fileUrl }))}
      />
      {error && <p className="text-destructive text-sm">{error}</p>}
    </div>
  )
}
